import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateInTimeZone } from '../../utils/timezone';

interface DatePickerProps {
  id?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  forceLight?: boolean;
  availableDates?: string[];
}

const DatePicker: React.FC<DatePickerProps> = ({
  id,
  name,
  value,
  onChange,
  label,
  min,
  max,
  required = false,
  disabled = false,
  error,
  className = '',
  forceLight = false,
  availableDates = [],
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? new Date(value) : null
  );

  // Mettre à jour la date sélectionnée quand la valeur change
  useEffect(() => {
    if (value) {
      setSelectedDate(new Date(value));
      setCurrentDate(new Date(value));
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);

    const formattedDate = formatDateInTimeZone(date, 'yyyy-MM-dd');

    const syntheticEvent = {
      target: { name, value: formattedDate },
      currentTarget: { name, value: formattedDate }
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];

    for (let i = startingDayOfWeek; i > 0; i--) {
      const prevDate = new Date(year, month, 1 - i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      days.push({ date: currentDate, isCurrentMonth: true });
    }

    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextDate = new Date(year, month + 1, day);
      days.push({ date: nextDate, isCurrentMonth: false });
    }

    return days;
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (date < todayMidnight) return true;

    if (min) {
      const minDate = new Date(min);
      minDate.setHours(0, 0, 0, 0);
      if (date < minDate) return true;
    }

    if (max) {
      const maxDate = new Date(max);
      maxDate.setHours(23, 59, 59, 999);
      if (date > maxDate) return true;
    }

    return false;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return dateMidnight.getTime() === todayMidnight.getTime();
  };

  const isSelected = (date: Date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const hasAvailability = (date: Date) => {
    const dateStr = formatDateInTimeZone(date, 'yyyy-MM-dd');
    return availableDates.includes(dateStr);
  };

  const days = getDaysInMonth(currentDate);
  const currentMonthName = formatDateInTimeZone(currentDate, 'MMMM');
  const currentYear = formatDateInTimeZone(currentDate, 'yyyy');

  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const getClasses = (lightClass: string, darkClass: string) => {
    return forceLight ? lightClass : `${lightClass} ${darkClass}`;
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          htmlFor={id || name}
          className={`block text-sm font-medium mb-3 ${getClasses('text-gray-700', 'dark:text-gray-300')}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className={`
        w-full rounded-2xl shadow-lg border overflow-hidden
        ${getClasses('bg-white border-gray-100', 'dark:bg-gray-800 dark:border-gray-700')}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}>
        {/* En-tête moderne avec navigation */}
        <div className={`
          flex items-center justify-between px-6 py-4
          ${getClasses('bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-200', 'dark:bg-gradient-to-r dark:from-gray-700 dark:to-gray-800 dark:border-gray-600')}
        `}>
          <button
            type="button"
            onClick={() => navigateMonth('prev')}
            disabled={disabled}
            className={`
              p-2 rounded-full transition-all duration-200
              ${getClasses(
                'hover:bg-white/70 hover:shadow-md active:scale-95',
                'dark:hover:bg-gray-600/50'
              )}
              ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <ChevronLeft className={`w-5 h-5 ${getClasses('text-primary-700', 'dark:text-gray-200')}`} />
          </button>

          <h2 className={`text-lg ${getClasses('text-primary-900', 'dark:text-gray-100')}`}>
            {currentMonthName} {currentYear}
          </h2>

          <button
            type="button"
            onClick={() => navigateMonth('next')}
            disabled={disabled}
            className={`
              p-2 rounded-full transition-all duration-200
              ${getClasses(
                'hover:bg-white/70 hover:shadow-md active:scale-95',
                'dark:hover:bg-gray-600/50'
              )}
              ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <ChevronRight className={`w-5 h-5 ${getClasses('text-primary-700', 'dark:text-gray-200')}`} />
          </button>
        </div>

        {/* Jours de la semaine avec style moderne */}
        <div className={`
          grid grid-cols-7 gap-1 px-3 py-3
          ${getClasses('bg-gray-50/50', 'dark:bg-gray-800/50')}
        `}>
          {dayNames.map(day => (
            <div
              key={day}
              className={`
                text-center text-xs uppercase tracking-wider
                ${getClasses('text-gray-600', 'dark:text-gray-400')}
              `}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Grille des jours avec animations */}
        <div className="grid grid-cols-7 gap-2 p-4">
          {days.map(({ date, isCurrentMonth }, index) => {
            const isDisabled = isDateDisabled(date);
            const today = isToday(date);
            const selected = isSelected(date);
            const available = hasAvailability(date);

            // Si on a des dates disponibles chargées et que ce jour n'en fait pas partie, on le grise
            const hasAvailabilityData = availableDates && availableDates.length > 0;
            const isUnavailable = hasAvailabilityData && !available && isCurrentMonth && !isDisabled;

            return (
              <button
                key={index}
                type="button"
                onClick={() => !isDisabled && !isUnavailable && isCurrentMonth && !disabled && handleDateSelect(date)}
                disabled={isDisabled || !isCurrentMonth || disabled || isUnavailable}
                className={`
                  relative aspect-square flex items-center justify-center
                  text-sm rounded-xl
                  transition-all duration-200 ease-out
                  ${!isCurrentMonth
                    ? getClasses('text-gray-300', 'dark:text-gray-600') + ' cursor-default'
                    : isDisabled || isUnavailable
                      ? getClasses('text-gray-300 bg-gray-50', 'dark:text-gray-600 dark:bg-gray-800') + ' cursor-not-allowed opacity-50'
                      : selected
                        ? 'bg-primary-600 text-gray-900 shadow-lg scale-105 ring-2 ring-primary-300 dark:ring-primary-700 font-bold'
                        : today
                          ? getClasses(
                              'bg-primary-100 text-primary-900 hover:bg-primary-200 hover:scale-105 hover:shadow-md',
                              'dark:bg-primary-900/40 dark:text-primary-200 dark:hover:bg-primary-800/60'
                            )
                          : getClasses(
                              'text-gray-700 hover:bg-primary-50 hover:scale-105 hover:shadow-md hover:text-primary-900',
                              'dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                            )
                  }
                `}
              >
                {date.getDate()}

                {/* Indicateur pour aujourd'hui */}
                {today && !selected && (
                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                )}

                {/* Indicateur de disponibilité - petit point vert */}
                {available && isCurrentMonth && !isDisabled && !selected && (
                  <div className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full ring-1 ring-white shadow-sm"></div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
          <span className="text-red-500">⚠</span>
          {error}
        </p>
      )}
    </div>
  );
};

export default DatePicker;
